import { msg } from "@lingui/core/macro";
import { Loader2, Plus, X } from "lucide-react";
import * as React from "react";

import { cn } from "../../lib/cn";
import { useI18n } from "../../lib/i18n";
import { SenderAvatar } from "../SenderAvatar";

/**
 * RecipientChips — premium controlled recipient editor (Superhuman /
 * Shortwave grade) used by the Conversazioni email reply compose.
 *
 * Each committed address renders as a CHIP: a warm derived-initials avatar
 * (:func:`SenderAvatar`, tone keyed by a hash of the address), the display
 * name / email, and an ✕ to remove. Clicking a chip lifts it back into the
 * inline text input for editing. Typing at the end shows an autocomplete
 * listbox when an ``onQuery`` transport is supplied (WAI-ARIA 1.2 combobox
 * pattern); when it isn't, the field degrades gracefully to free-text entry
 * with comma / Enter / Tab / blur committing a validated address.
 *
 * Design contract (pencil aesthetic):
 *   - warm cream field, hairline border, NO orange wash (orange is reserved
 *     for the Send button). Avatar tints stay in the warm family
 *     (orange / amber / stone / emerald / rose / lime) — never cold.
 *   - Backspace on an empty input removes the trailing chip.
 *   - an invalid typed address flips a subtle rose error state instead of
 *     silently dropping the text.
 *
 * This is the RICH sibling of ``ando/RecipientsInput`` (plain pills, wired to
 * the ActionFieldEditor people-search): RecipientChips reuses the shared
 * ``SenderAvatar`` primitive and a decoupled ``onQuery`` transport so it can
 * plug into either the mention search or a contacts endpoint.
 */

/** One autocomplete hit surfaced by the ``onQuery`` transport. */
export interface RecipientChipsSuggestion {
  readonly id: string;
  readonly display: string;
  readonly email: string;
  readonly kind: string;
}

export interface RecipientChipsProps {
  /** Visible + accessible label for the field (e.g. "A", "Cc", "Ccn"). */
  readonly label: string;
  /** Current committed addresses (raw strings — may be ``Name <email>``). */
  readonly value: readonly string[];
  /** Emitted whenever the list changes (chip added / removed / edited). */
  readonly onChange: (next: string[]) => void;
  /**
   * Async autocomplete transport. When omitted the field degrades to plain
   * free-text chip entry (still fully editable + validated).
   */
  readonly onQuery?: (q: string) => Promise<readonly RecipientChipsSuggestion[]>;
  readonly placeholder?: string;
  readonly autoFocus?: boolean;
  readonly className?: string;
  /**
   * Drop the field's own border / background / focus-ring so it can sit as a
   * bare row inside a shared "letter" sheet (the unified compose). The flex
   * layout + label stay; only the chrome is removed.
   */
  readonly bare?: boolean;
}

// Mirrors the BE ``_EMAIL_PATTERN`` + the FE ``EMAIL_RE`` used by
// ``RecipientsInput`` / ``ActionFieldEditor`` — kept in lockstep so an
// address accepted here is also accepted by the send validator.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Warm avatar tones (Tailwind classes verbatim — DON'T interpolate, or the
// JIT purge would drop them). Deliberately warm-only per the pencil design
// language: no cold violet / sky / blue.
const WARM_TONES: ReadonlyArray<{ bg: string; fg: string }> = [
  { bg: "bg-orange-50", fg: "text-orange-700" },
  { bg: "bg-amber-50", fg: "text-amber-700" },
  { bg: "bg-stone-100", fg: "text-stone-700" },
  { bg: "bg-emerald-50", fg: "text-emerald-700" },
  { bg: "bg-rose-50", fg: "text-rose-700" },
  { bg: "bg-lime-50", fg: "text-lime-700" },
];

/** Stable djb2 hash → warm-tone index, keyed by the address. */
function warmTone(seed: string): { bg: string; fg: string } {
  let h = 5381;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 33) ^ seed.charCodeAt(i);
  }
  const idx = Math.abs(h) % WARM_TONES.length;
  return WARM_TONES[idx] ?? { bg: "bg-stone-100", fg: "text-stone-700" };
}

/** Split ``Mario Rossi <m@x.it>`` (or a bare email) into display + email. */
export function parseRecipient(raw: string): { display: string; email: string } {
  const trimmed = raw.trim();
  const m = trimmed.match(/<([^>]+)>/);
  if (m?.[1]) {
    const email = m[1].trim();
    const display =
      trimmed
        .replace(/<[^>]+>/, "")
        .trim()
        .replace(/^["']|["']$/g, "") || email;
    return { display, email };
  }
  return { display: trimmed, email: trimmed };
}

/** Whether a raw recipient string carries a valid email address. */
export function isValidRecipient(raw: string): boolean {
  return EMAIL_RE.test(parseRecipient(raw).email);
}

export const RecipientChips: React.FC<RecipientChipsProps> = ({
  label,
  value,
  onChange,
  onQuery,
  placeholder,
  autoFocus,
  className,
  bare,
}) => {
  const { i18n } = useI18n();
  const reactId = React.useId();
  const fieldId = `recipient-chips-${reactId}`;
  const selected = React.useMemo(() => [...value], [value]);

  const [draft, setDraft] = React.useState("");
  const [invalid, setInvalid] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [hits, setHits] = React.useState<readonly RecipientChipsSuggestion[]>([]);
  const [activeIdx, setActiveIdx] = React.useState(-1);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  // Monotonic request id so a slow ``onQuery`` can't overwrite the hits of a
  // newer keystroke (no AbortSignal in the transport contract).
  const queryIdRef = React.useRef(0);

  // Programmatic autofocus (avoids the JSX ``autoFocus`` attribute the linter
  // flags) — runs once on mount when the host opts in for the primary field.
  React.useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
    // Mount-only: the host toggles autofocus per field, never mid-life.
  }, [autoFocus]);

  const commit = React.useCallback(
    (raw: string): boolean => {
      const v = raw.trim().replace(/[,;]$/, "");
      if (!v) return false;
      if (!isValidRecipient(v)) {
        setInvalid(true);
        return false;
      }
      const email = parseRecipient(v).email.toLowerCase();
      const dup = selected.some((s) => parseRecipient(s).email.toLowerCase() === email);
      if (!dup) onChange([...selected, v]);
      setDraft("");
      setInvalid(false);
      setActiveIdx(-1);
      return true;
    },
    [onChange, selected],
  );

  const removeAt = React.useCallback(
    (idx: number) => {
      onChange(selected.filter((_, i) => i !== idx));
    },
    [onChange, selected],
  );

  /** Lift a chip back into the input for inline editing (Superhuman UX). */
  const editAt = React.useCallback(
    (idx: number) => {
      const target = selected[idx];
      if (target === undefined) return;
      onChange(selected.filter((_, i) => i !== idx));
      setDraft(target);
      setInvalid(false);
      inputRef.current?.focus();
    },
    [onChange, selected],
  );

  // Debounced autocomplete. Skipped entirely when no transport is wired so
  // free-text mode never burns a request.
  React.useEffect(() => {
    if (!onQuery || !open) {
      setHits([]);
      return;
    }
    const q = draft.trim();
    let cancelled = false;
    queryIdRef.current += 1;
    const id = queryIdRef.current;
    setBusy(true);
    const t = setTimeout(() => {
      void onQuery(q)
        .then((res) => {
          if (cancelled || id !== queryIdRef.current) return;
          setHits(res);
          setActiveIdx(-1);
        })
        .catch(() => {
          if (cancelled || id !== queryIdRef.current) return;
          setHits([]);
        })
        .finally(() => {
          if (!cancelled && id === queryIdRef.current) setBusy(false);
        });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [draft, open, onQuery]);

  // Close the listbox when focus leaves the field.
  React.useEffect(() => {
    if (!open) return;
    const onDocFocus = (ev: FocusEvent) => {
      const root = containerRef.current;
      const target = ev.target as Node | null;
      if (root && target && root.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener("focusin", onDocFocus);
    return () => document.removeEventListener("focusin", onDocFocus);
  }, [open]);

  const filteredHits = React.useMemo(() => {
    const chosen = new Set(selected.map((s) => parseRecipient(s).email.toLowerCase()));
    return hits.filter((h) => !chosen.has(h.email.toLowerCase()));
  }, [hits, selected]);

  const trimmedDraft = draft.trim();
  const canAddFree =
    isValidRecipient(trimmedDraft) &&
    !selected.some((s) => parseRecipient(s).email.toLowerCase() === trimmedDraft.toLowerCase()) &&
    !filteredHits.some((h) => h.email.toLowerCase() === trimmedDraft.toLowerCase());

  const optionId = (idx: number) => `${fieldId}-opt-${idx}`;
  const listboxId = `${fieldId}-listbox`;
  // Stay open while the user is actively searching (has typed something, a query
  // is in flight, or hits exist) so the listbox never "closes by itself" the
  // instant a query returns no matches — it shows a clear empty state instead.
  const showListbox =
    open && Boolean(onQuery) && (trimmedDraft.length > 0 || busy || filteredHits.length > 0);
  const activeDescendant =
    showListbox && activeIdx >= 0 && activeIdx < filteredHits.length
      ? optionId(activeIdx)
      : undefined;

  const commitHitAt = (idx: number) => {
    const hit = filteredHits[idx];
    if (!hit) return;
    commit(hit.email);
    inputRef.current?.focus();
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        "relative flex flex-wrap items-center gap-1.5 transition-colors",
        bare
          ? "px-3 py-1.5"
          : cn(
              "rounded-md border bg-white/55 px-2 py-1.5 focus-within:ring-2 focus-within:ring-ando-orange",
              invalid ? "border-rose-300" : "border-hairline",
            ),
        className,
      )}
    >
      <span
        id={`${fieldId}-label`}
        className="w-8 shrink-0 select-none font-medium text-[10px] text-ink-3 uppercase tracking-[0.05em]"
      >
        {label}
      </span>
      {selected.map((raw, idx) => {
        const { display, email } = parseRecipient(raw);
        const tone = warmTone(email.toLowerCase());
        return (
          <span
            // Emails are de-duplicated on commit, so the address is a stable
            // unique key across the list.
            key={email.toLowerCase()}
            className="group inline-flex max-w-full items-center gap-1.5 rounded-full border border-hairline bg-white/80 py-0.5 pr-1 pl-0.5 text-[12px] text-ink-1"
          >
            <button
              type="button"
              onClick={() => editAt(idx)}
              className={cn(
                "inline-flex min-w-0 items-center gap-1.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange",
                // Senza avatar il chip parte troppo attaccato al bordo.
                display === email && "pl-1.5",
              )}
              aria-label={i18n._(msg`Modifica ${display}`)}
            >
              {/* Avatar solo quando c'è un nome vero: le iniziali di un
                  indirizzo nudo ("NO" per noreply@…) non identificano
                  nessuno, sono solo rumore accanto al testo che le ripete. */}
              {display === email ? null : (
                <SenderAvatar sender={display} toneBg={tone.bg} toneFg={tone.fg} size={18} />
              )}
              <span className="truncate">{display}</span>
            </button>
            <button
              type="button"
              aria-label={i18n._(msg`Rimuovi ${display}`)}
              onClick={(e) => {
                e.stopPropagation();
                removeAt(idx);
              }}
              className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-ink-3 transition-colors hover:bg-black/[0.06] hover:text-ink-1"
            >
              <X className="h-3 w-3" strokeWidth={2} aria-hidden />
            </button>
          </span>
        );
      })}
      <input
        ref={inputRef}
        id={fieldId}
        type="text"
        inputMode="email"
        autoComplete="off"
        // biome-ignore lint/a11y/noAutofocus: opt-in via prop for the primary To field only
        autoFocus={autoFocus}
        role="combobox"
        aria-expanded={showListbox}
        aria-controls={listboxId}
        aria-label={label}
        aria-labelledby={`${fieldId}-label`}
        aria-autocomplete="list"
        aria-activedescendant={activeDescendant}
        aria-invalid={invalid || undefined}
        value={draft}
        placeholder={selected.length === 0 ? placeholder : ""}
        className="min-w-[140px] flex-1 bg-transparent text-[12px] text-ink-1 placeholder:text-ink-3 focus:outline-none"
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          setDraft(e.target.value);
          setInvalid(false);
          setActiveIdx(-1);
          if (!open) setOpen(true);
        }}
        onBlur={() => {
          // Commit a valid typed address on blur; leave an invalid one in
          // place (with its error state) so the user never loses their text.
          if (draft.trim()) commit(draft);
        }}
        onKeyDown={(e) => {
          if (showListbox && filteredHits.length > 0) {
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setActiveIdx((i) => (i + 1 >= filteredHits.length ? 0 : i + 1));
              return;
            }
            if (e.key === "ArrowUp") {
              e.preventDefault();
              setActiveIdx((i) => (i <= 0 ? filteredHits.length - 1 : i - 1));
              return;
            }
            if (e.key === "Enter" && activeIdx >= 0) {
              e.preventDefault();
              commitHitAt(activeIdx);
              return;
            }
          }
          if (e.key === "Enter" || e.key === "," || e.key === ";" || e.key === "Tab") {
            if (draft.trim()) {
              // Tab still moves focus when the field is empty; only intercept
              // it to commit a pending address.
              e.preventDefault();
              commit(draft);
            }
          } else if (e.key === "Backspace" && draft === "" && selected.length > 0) {
            removeAt(selected.length - 1);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />
      {busy ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-ink-3" aria-hidden />
      ) : null}

      {showListbox ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label={i18n._(msg`Suggerimenti destinatari`)}
          tabIndex={-1}
          className="absolute top-full right-0 left-0 z-50 mt-1 max-h-64 overflow-auto rounded-md border border-hairline bg-white shadow-lg ring-1 ring-black/5"
        >
          {canAddFree ? (
            <button
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                commit(trimmedDraft);
              }}
              className="flex w-full items-center gap-2 border-hairline border-b px-3 py-1.5 text-left text-[13px] text-ink-1 hover:bg-ando-orange-soft/40"
            >
              <Plus className="h-3.5 w-3.5 shrink-0 text-ando-orange" aria-hidden />
              <span className="truncate">{i18n._(msg`Aggiungi ${trimmedDraft}`)}</span>
            </button>
          ) : null}
          {busy && filteredHits.length === 0 ? (
            <div className="flex items-center gap-2 px-3 py-3 text-[12px] text-ink-3">
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              {i18n._(msg`Cerco tra i contatti…`)}
            </div>
          ) : filteredHits.length > 0 ? (
            filteredHits.map((h, idx) => {
              const tone = warmTone(h.email.toLowerCase());
              return (
                <div
                  key={`${h.kind}:${h.id}:${h.email}`}
                  id={optionId(idx)}
                  role="option"
                  aria-selected={idx === activeIdx}
                  tabIndex={-1}
                  onMouseEnter={() => setActiveIdx(idx)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    commitHitAt(idx);
                  }}
                  className={cn(
                    "flex w-full cursor-pointer items-center gap-2.5 px-3 py-1.5 text-left transition-colors",
                    idx === activeIdx
                      ? "bg-ando-orange-soft/60 text-ink-1"
                      : "hover:bg-black/[0.03]",
                  )}
                >
                  <SenderAvatar sender={h.display} toneBg={tone.bg} toneFg={tone.fg} size={24} />
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-medium text-[13px] text-ink-1">
                      {h.display || h.email}
                    </span>
                    <span className="truncate text-[11.5px] text-ink-3">{h.email}</span>
                  </span>
                </div>
              );
            })
          ) : canAddFree ? null : (
            <div className="px-3 py-3 text-[12px] text-ink-3">
              {i18n._(msg`Nessun contatto trovato`)}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};
RecipientChips.displayName = "RecipientChips";
